import React, { useCallback, useMemo, useState } from 'react';
import { StyleSheet, View } from 'react-native';
import { useSelector } from 'react-redux';
import {
  NavigationProp,
  StackActions,
  useNavigation,
} from '@react-navigation/native';
import {
  Divider,
  Icon,
  IconProps,
  Layout,
  Popover,
  Text,
} from '@ui-kitten/components';
import lodashIsEmpty from 'lodash/isEmpty';

import routeList, {
  AccountNavigatorProps,
  BasketNavigatorProps,
  ReservationNavigatorProps,
} from '../../Routes/list';
import { isLoggedInSelector, userSelector } from '../../RTK/user/selectors';
import ThemeColor from '../../Theme/colors';
import ThemeStyle from '../../Theme/styles';
import {
  ids as platformStyleIds,
  styles as platformStyles,
} from '../../Theme/styles/platform';

import Button from '../Button';
import List from '../List';

const PopupProfile = () => {
  const navigation =
    useNavigation<
      NavigationProp<
        AccountNavigatorProps & BasketNavigatorProps & ReservationNavigatorProps
      >
    >();
  const isLoggedIn = useSelector(isLoggedInSelector);
  const user = useSelector(userSelector);

  const [isVisible, setVisible] = useState(false);

  const list = useMemo(() => {
    return [
      {
        iconName: 'user-circle',
        name: 'Account',
        onPress: () => navigation.navigate(routeList.PROFILE),
      },
      // {
      //   iconName: 'book',
      //   name: 'Addresses',
      //   onPress: () =>
      //     alert(
      //       'Addresses is inside popover too, still on discussion what to do here'
      //     ),
      // },
      {
        iconName: 'history',
        name: 'Order History',
        onPress: () =>
          navigation.navigate(routeList.BASKET_TAB, { activeTab: 1 }),
      },
      {
        iconName: 'ticket',
        name: 'Reservations',
        onPress: () => {
          setVisible(false);
          navigation.dispatch(
            StackActions.replace(routeList.HOME, {
              screen: 'MainMenu',
              params: {
                screen: routeList.RESERVATION_TAB,
                params: {
                  screen: routeList.MY_RESERVATIONS,
                },
              },
            }),
          );
        },
      },
      // {
      //   iconName: 'cog',
      //   name: 'Settings',
      //   onPress: () => alert('Is settings needed?'),
      // },
    ];
  }, [navigation]);

  const accessoryLeft = useCallback(
    (props: IconProps) => (
      <Icon
        {...props}
        name="person"
        fill={ThemeColor.black}
        style={[props.style, styles.iconLeft]}
      />
    ),
    [],
  );

  const accessoryRight = useCallback(
    (props: IconProps) => (
      <Icon
        {...props}
        fill={ThemeColor.black}
        style={[props.style, styles.iconRight]}
        name="chevron-down"
      />
    ),
    [],
  );

  const popupAnchor = useCallback(
    () => (
      <Button
        appearance="ghost"
        accessoryLeft={accessoryLeft}
        accessoryRight={accessoryRight}
        size="small"
        style={styles.button}
        onPress={() => setVisible(true)}
      >
        {() => (
          <View style={ThemeStyle.flexDirectionRowCenter}>
            <Text>{user?.first_name}</Text>
            <Text
              dataSet={{ media: platformStyleIds.webOnly }}
              style={platformStyles.webOnly}
            >
              &nbsp;
              {user?.last_name}
            </Text>
          </View>
        )}
      </Button>
    ),
    [user?.first_name, user?.last_name, accessoryLeft, accessoryRight],
  );

  if (!isLoggedIn || lodashIsEmpty(user)) return null;

  return (
    <View style={ThemeStyle.spacingLeftMedium}>
      <Popover
        anchor={popupAnchor}
        animationType="fade"
        placement="bottom end"
        visible={isVisible}
        onBackdropPress={() => setVisible(false)}
      >
        <Layout style={styles.popupContainer}>
          <List
            data={list}
            scrollEnabled={false} // no need scroll here
            ItemSeparatorComponent={() => (
              <Divider style={[ThemeStyle.divider]} />
            )}
            renderItem={({ item }) => (
              <Button onPress={item.onPress} plain>
                <View style={styles.popupItemContainer}>
                  <View
                    style={[
                      styles.popupIcon,
                      ThemeStyle.spacingLeftSmall,
                      ThemeStyle.spacingRightSmall,
                    ]}
                  >
                    {React.isValidElement(item.icon) ? (
                      item.icon
                    ) : (
                      <Icon
                        fill={ThemeColor.black}
                        name={item.iconName}
                        pack="fa"
                        style={styles.popupIcon}
                      />
                    )}
                  </View>
                  <Text>{item.name}</Text>
                </View>
              </Button>
            )}
          />
        </Layout>
      </Popover>
    </View>
  );
};

const styles = StyleSheet.create({
  button: {
    borderWidth: 1,
    borderColor: ThemeColor.black,
  },
  iconLeft: {
    marginHorizontal: 0,
    marginLeft: 10,
    marginRight: 5,
    width: 20,
    height: 20,
  },
  iconRight: {
    marginHorizontal: 0,
    marginLeft: 5,
    marginRight: 10,
  },
  popupContainer: {
    ...ThemeStyle.spacingTopSmall,
    ...ThemeStyle.pageBackground,
    ...ThemeStyle.pageHorizontalSpacingSmall,
    borderWidth: 1,
    borderColor: ThemeColor.black,
    width: 210,
  },
  popupItemContainer: {
    ...ThemeStyle.pageVerticalSpacingMedium,
    ...ThemeStyle.flexDirectionRowCenter,
  },
  popupIcon: {
    width: 20,
    height: 20,
  },
});

export default PopupProfile;
